import axios, { AxiosProgressEvent } from "axios";
import customAxios from "config";
import { renterError } from "utils/errorMessage";

export const Booking = async (payload: any): Promise<any> => {
  try {
    const response = await customAxios.post<any>("/api/bookings/", payload);

    return response.data;
  } catch (error) {
    return { error: renterError(error, "booking") };
  }
};

export const cancelBooking = async (
  booking_id: string,
): Promise<{ data?: any; error?: string }> => {
  try {
    const response = await customAxios.post<any>(
      `/api/bookings/${booking_id}/cancel/`,
    );
    return { data: response.data }; // Return data when successful
  } catch (error: any) {
    return { error: renterError(error, "cancellation") };
  }
};

export const getBooking = async (): Promise<{ data?: any; error?: string }> => {
  try {
    const response = await customAxios.get<any>("/api/bookings/");

    return response.data;
  } catch (error) {
    return { error: renterError(error, "booking") };
  }
};

export const retriveBooking = async (
  bookingId: string,
): Promise<{ data?: any; error?: string }> => {
  try {
    const response = await customAxios.get<any>(`/api/bookings/${bookingId}/`);

    return response.data;
  } catch (error) {
    return { error: renterError(error, "booking") };
  }
};

export const getTripPhotos = async (
  bookingId: string,
): Promise<{ data?: any; error?: string }> => {
  try {
    const response = await customAxios.get<any>(
      `/api/fleet/upload-fleet-images/?booking_id=${bookingId}`,
    );

    return response.data;
  } catch (error) {
    return { error: renterError(error, "photos") };
  }
};

export const uploadTripPhotos = async (
  files: File[],
  bookingId: string,
  preTrip: boolean,
) => {
  const formData = new FormData();

  // Append each file to the form data
  for (let i = 0; i < files.length; i++) {
    formData.append("images", files[i]); // The key 'images' will match the backend expected form field name
  }

  // Append booking ID and image type
  formData.append("booking_id", bookingId); // Append the booking ID
  formData.append("image_type", preTrip ? "pre_trip" : "post_trip"); // Append image type based on preTrip flag

  try {
    const response = await customAxios.post(
      `/api/fleet/upload-fleet-images/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data", // Important for sending form data
        },
      },
    );

    // Assuming the response will contain the uploaded photo details
    return response.data;
  } catch (error) {
    return { error: renterError(error, "photos") };
  }
};

/**
 * The photo id in the path is sequential and a photo carries no uuid of its
 * own, so the backend will not delete one for a caller it cannot scope to a
 * company unless the booking is named too — by uuid, which is the entitlement
 * (1NOW-10707). Knowing the booking uuid proves the caller holds the booking;
 * knowing a small number proves nothing.
 */
export const deleteTripPhoto = async (id: number, bookingId: string) => {
  try {
    const response = await customAxios.delete(
      `/api/fleet/upload-fleet-images/${id}/?booking=${encodeURIComponent(bookingId)}`,
    );
    return response.data;
  } catch (error) {
    return { error: renterError(error, "photos") };
  }
};

export const getClaimPhotos = async (
  bookingId: string,
): Promise<{ data?: any; error?: string }> => {
  try {
    const response = await customAxios.get<any>(
      `/api/fleet/upload-fleet-claim-images/?booking_id=${bookingId}`,
    );

    return response.data;
  } catch (error) {
    return { error: renterError(error, "photos") };
  }
};

export const uploadClaimPhotos = async (
  files: File[],
  bookingId: string,
  preTrip: boolean,
) => {
  const formData = new FormData();

  for (let i = 0; i < files.length; i++) {
    formData.append("images", files[i]);
  }

  formData.append("booking_id", bookingId);
  formData.append("image_type", preTrip ? "pre_trip" : "post_trip");

  try {
    const response = await customAxios.post(
      `/api/fleet/upload-fleet-claim-images/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data;
  } catch (error) {
    return { error: renterError(error, "photos") };
  }
};

/** Same rule as deleteTripPhoto — the claim endpoint is the same view. */
export const deleteClaimPhoto = async (id: number, bookingId: string) => {
  try {
    const response = await customAxios.delete(
      `/api/fleet/upload-fleet-claim-images/${id}/?booking=${encodeURIComponent(bookingId)}`,
    );
    return response.data;
  } catch (error) {
    return { error: renterError(error, "photos") };
  }
};

// 1NOW-688: fetch the server-authoritative Bonzah confirmation copy for a
// proposed edit (change/contract/extend). Returns { message: { type, title,
// message, date_info? } } — an empty message means no popup is needed.
export const getBonzahAlerts = async (
  bookingId: any,
  payload: {
    pick_up_time: string;
    drop_off_time: string;
    bonzah_insurance_options: any[];
  },
): Promise<{ message?: any; error?: string }> => {
  try {
    const response = await customAxios.post<any>(
      `/api/bookings/${bookingId}/bonzah_alerts/`,
      payload,
    );

    return response.data;
  } catch (error) {
    return { error: renterError(error, "booking") };
  }
};

export const tripModificationRequest = async (
  payload: any,
): Promise<{
  data?: any;
  error?: string;
  auto_approved?: boolean;
  auto_approve_failed?: boolean;
}> => {
  try {
    const response = await customAxios.post<any>(
      "/api/bookings/req_modification/",
      payload,
    );

    return response.data;
  } catch (error) {
    return { error: renterError(error, "booking") };
  }
};

export const getPreviousBooking = async (
  booking_id?: string,
  offset?: number,
): Promise<{ data?: any; error?: string }> => {
  try {
    const endpoint = `/api/bookings/other_bookings_of_the_booking_user/?booking_uuid=${booking_id}&paginated=true&offset=${offset}`;
    const response = await customAxios.get<any>(endpoint);

    return response;
  } catch (error) {
    return { error: renterError(error, "booking") };
  }
};
export const getConnectedBooking = async (
  booking_id?: string,
  offset?: number,
): Promise<{ data?: any; error?: string }> => {
  try {
    const endpoint = `/api/bookings/${booking_id}/subsequent_bookings_incl_main`;
    const response = await axios.get<any>(endpoint, {
      baseURL: (customAxios.defaults.baseURL as string) || undefined,
      // Explicitly send an empty Authorization header so the request does not include a token
      headers: {
        Authorization: "",
      },
    });

    return response;
  } catch (error) {
    return { error: renterError(error, "booking") };
  }
};
